import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CompanyManagementService from "src/Services/API/companymanagement.service";
import { isSuperAdmin } from "src/Common/function";
import { setActingTenant } from "src/Common/tenant";
import Loader from "src/Components/Common/Loader";
import AddCompanyModal from "./AddCompanyModal";
import AddAdminModal from "./AddAdminModal";
import DeleteTenantDialog from "./DeleteTenantDialog";

const RESEND_COOLDOWN_MS = 60000;

function CompanyManagement() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddCompany, setIsAddCompany] = useState(false);
  const [adminForCompany, setAdminForCompany] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  // Per-invite resend cooldown started locally: { [inviteId]: epochMs }.
  const [cooldownUntil, setCooldownUntil] = useState({});
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Defend the page against direct URL access — the menu item is already gated,
  // but the route should not render for anyone but a Super Admin.
  useEffect(() => {
    let allowed = false;
    try {
      allowed = isSuperAdmin();
    } catch (e) {
      allowed = false;
    }
    if (!allowed) {
      navigate("/");
      return;
    }
    getCompanies();
  }, []);

  const getCompanies = async () => {
    setIsLoading(true);
    try {
      const response = await CompanyManagementService.list();
      if (response.status === 200) {
        const rows = (response.data || []).map((item) => ({
          ...item,
          formattedCreatedAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-GB") : "",
        }));
        setCompanies(rows);
      } else {
        toast("Something went wrong!");
      }
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load tenants");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCompany = (data) => {
    CompanyManagementService.create(data)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast("Tenant created and administrator invited");
          getCompanies();
        } else {
          toast("Something went wrong");
        }
      })
      .catch((error) => {
        toast(error?.response?.data?.message || "Failed to create tenant");
      });
    setIsAddCompany(false);
  };

  const handleAddAdmin = (data) => {
    CompanyManagementService.addAdmin(data)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast("Administrator invited");
          getCompanies();
        } else {
          toast("Something went wrong");
        }
      })
      .catch((error) => {
        toast(error?.response?.data?.message || "Failed to invite administrator");
      });
    setAdminForCompany(null);
  };

  // The dialog only enables its button once the identifier is typed, and sends
  // it back so the server can verify the id and name name the same tenant.
  const handleDelete = (company) => {
    if (!company) return;
    setIsDeleting(true);
    CompanyManagementService.remove(company.id, company.identifier)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast("Tenant and all its data deleted");
          setCompanyToDelete(null);
          getCompanies();
        } else {
          toast("Something went wrong");
        }
      })
      .catch((error) => {
        toast(error?.response?.data?.message || "Failed to delete tenant");
      })
      .finally(() => setIsDeleting(false));
  };

  // Enter a tenant: every API call and asset URL switches to its company, so
  // the normal pages show that tenant's data. Exit via the sidebar banner.
  //
  // Full reload rather than navigate(): entering changes the company every
  // request is scoped to, so the tenant's vocabulary, the caller's permissions
  // and current-user profile all have to be re-fetched, and any Redux/component
  // state cached for the previous context dropped. App's reload effect is keyed
  // on the acting tenant, but App does not re-render on a client-side navigate
  // (its body doesn't read the location), so that effect never fires on a
  // switch — a reload is the reliable way to pick the new tenant up everywhere.
  const enterTenant = (company) => {
    setActingTenant({ identifier: company.identifier, name: company.name });
    window.location.assign("/work");
  };

  // Latest administrator invite for the row (list is newest-first).
  const latestInvite = (company) => company.adminInvites?.[0] || null;

  // Cooldown deadline: the later of the server's last-send time and any local
  // cooldown we just started, so it survives a page reload.
  const getCooldownUntil = (invite) => {
    const fromServer = invite.updatedAt ? new Date(invite.updatedAt).getTime() + RESEND_COOLDOWN_MS : 0;
    const fromLocal = cooldownUntil[invite.id] || 0;
    return Math.max(fromServer, fromLocal);
  };

  const resendInvite = (invite) => {
    if (!invite || resendingId === invite.id) return;
    if (getCooldownUntil(invite) > Date.now()) return;
    setResendingId(invite.id);
    CompanyManagementService.resendInvite(invite.id)
      .then(() => {
        toast("Invitation resent successfully");
        setCooldownUntil((prev) => ({ ...prev, [invite.id]: Date.now() + RESEND_COOLDOWN_MS }));
      })
      .catch((error) => {
        const data = error.response?.data;
        if (error.response?.status === 429 && data?.retryAfter) {
          setCooldownUntil((prev) => ({
            ...prev,
            [invite.id]: Date.now() + data.retryAfter * 1000,
          }));
        }
        toast(data?.message || "Failed to resend invitation");
      })
      .finally(() => {
        setResendingId(null);
      });
  };

  const renderAdminInvite = (company) => {
    const invite = latestInvite(company);
    if (!invite) {
      return <span className="text-[14px] font-OpenSansRegular text-[#5F6477]">—</span>;
    }
    // The invitee's name is only on invites sent after it became a required
    // field, so fall back to the email alone.
    const who = invite.name ? `${invite.name} · ${invite.email}` : invite.email;
    if (invite.accepted) {
      return (
        <div className="min-w-0">
          <span className="text-[12px] leading-[18px] font-OpenSansSemiBold text-green-600">Accepted</span>
          <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#5F6477] truncate">{who}</p>
        </div>
      );
    }
    const cooldownLeft = getCooldownUntil(invite) - now;
    const coolingDown = cooldownLeft > 0;
    return (
      <div className="min-w-0 flex items-center gap-2">
        <div className="min-w-0">
          <span className="text-[12px] leading-[18px] font-OpenSansSemiBold text-orange-500">Pending</span>
          <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#5F6477] truncate">{who}</p>
        </div>
        <span
          onClick={() => resendInvite(invite)}
          className={`text-[12px] font-OpenSansSemiBold shrink-0 ${
            resendingId === invite.id || coolingDown
              ? "text-gray-400 cursor-not-allowed"
              : "text-blueColor cursor-pointer"
          }`}
        >
          {coolingDown ? `${Math.ceil(cooldownLeft / 1000)}s` : "Resend"}
        </span>
      </div>
    );
  };

  return (
    <div className="w-full h-full bg-backgroundColor p-5 flex flex-col min-h-0">
      {isAddCompany && (
        <AddCompanyModal onAddClicked={handleAddCompany} onCancelClicked={() => setIsAddCompany(false)} />
      )}
      {adminForCompany && (
        <AddAdminModal
          company={adminForCompany}
          onAddClicked={handleAddAdmin}
          onCancelClicked={() => setAdminForCompany(null)}
        />
      )}
      <DeleteTenantDialog
        company={companyToDelete}
        isDeleting={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setCompanyToDelete(null)}
      />

      <div className="flex items-center justify-end mb-4">
        <div
          onClick={() => setIsAddCompany(true)}
          className="px-4 h-[30px] flex items-center justify-center cursor-pointer bg-blueColor rounded-[6px]"
        >
          <span className="text-[12px] font-OpenSansSemiBold text-white">+ Tenant</span>
        </div>
      </div>

      {/* Same table chrome as the Work Orders / Team tables. */}
      <div
        className="w-full flex-1 min-h-0 flex flex-col overflow-hidden"
        style={{
          borderRadius: "12px",
          border: "1px solid #E5E8F3",
          background: "#FFF",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
          <table className="w-full min-w-[1180px] table-fixed">
            <thead className="sticky top-0 z-10">
              <tr style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}>
                {[
                  { label: "No.", width: "w-[70px]" },
                  { label: "Name", width: "" },
                  { label: "Identifier", width: "w-[140px]" },
                  { label: "Users", width: "w-[80px]" },
                  { label: "Created", width: "w-[140px]" },
                  { label: "Admin Invite", width: "w-[280px]" },
                  { label: "Actions", width: "w-[260px]" },
                ].map((column) => (
                  <th key={column.label} className={column.width}>
                    <div className="font-OpenSansSemiBold text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                      {column.label}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7}>
                    <Loader variant="block" className="py-12" ariaLabel="loading-tenants" />
                  </td>
                </tr>
              )}

              {!isLoading && companies.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="px-4 py-6 text-[14px] font-OpenSansRegular text-[#5F6477]">No tenants yet.</div>
                  </td>
                </tr>
              )}

              {!isLoading && companies.map((company, i) => (
                <tr key={company.id} className="border-b border-borderColor hover:bg-[#FAFAFB]">
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[12px] text-[#717680]">{i + 1}</div>
                  </td>
                  <td>
                    <div className="px-4 py-3 font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate">
                      {company.name}
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3 font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477] truncate">
                      {company.identifier}
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3 font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                      {company.userCount}
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3 font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                      {company.formattedCreatedAt}
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3">{renderAdminInvite(company)}</div>
                  </td>
                  <td>
                    <div className="px-4 py-3 flex items-center gap-3">
                      <span
                        onClick={() => enterTenant(company)}
                        className="text-[12px] font-OpenSansSemiBold text-white bg-blueColor rounded-[6px] px-3 py-1 cursor-pointer"
                      >
                        Enter
                      </span>
                      <span
                        onClick={() => setAdminForCompany(company)}
                        className="text-[12px] font-OpenSansSemiBold text-blueColor cursor-pointer"
                      >
                        Add Admin
                      </span>
                      <span
                        onClick={() => setCompanyToDelete(company)}
                        className="text-[12px] font-OpenSansSemiBold cursor-pointer text-red-500"
                      >
                        Delete
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CompanyManagement;
